import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { CheckCircle, AlertCircle, Tag, RefreshCw, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL;

const CONFIDENCE_COLORS = {
  high: 'bg-green-100 text-green-700 border-green-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-red-100 text-red-700 border-red-200'
};

const AutoTagTool = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [appliedIds, setAppliedIds] = useState(new Set());

  const runDryRun = async () => {
    setLoading(true);
    setResults(null);
    try {
      const response = await axios.post(`${API}/api/admin/auto-tag-articles?dry_run=true`);
      setResults(response.data);
    } catch (error) {
      toast.error('Error analyzing articles');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyAllTags = async () => {
    if (!window.confirm(`Apply tags to ${results.matched_articles} articles? This will update their subcategory and pillar fields.`)) return;
    setApplying(true);
    try {
      const response = await axios.post(`${API}/api/admin/auto-tag-articles?dry_run=false`);
      setResults(response.data);
      setAppliedIds(new Set(response.data.suggestions.map(s => s.article_id)));
      toast.success(`Tagged ${response.data.applied} articles successfully!`);
    } catch (error) {
      toast.error('Error applying tags');
      console.error(error);
    } finally {
      setApplying(false);
    }
  };

  const applySingleTag = async (suggestion) => {
    try {
      const tags = suggestion.all_matches.map(m => m.name.toLowerCase());
      await axios.post(`${API}/api/admin/apply-article-tag`, {
        article_id: suggestion.article_id,
        subcategory: suggestion.suggested_subcategory,
        pillar: suggestion.suggested_pillar,
        tags
      });
      setAppliedIds(prev => new Set([...prev, suggestion.article_id]));
      toast.success(`Tagged "${suggestion.title}"`);
    } catch (error) {
      toast.error('Error applying tag');
    }
  };

  return (
    <div className="space-y-4" data-testid="auto-tag-tool">
      {/* Header */}
      <div className="bg-white rounded-xl border border-burgundy/15 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-serif font-bold text-burgundy text-lg flex items-center gap-2">
              <Tag className="h-5 w-5" /> Auto-Tag Articles
            </h3>
            <p className="text-burgundy/60 text-sm mt-1">
              Automatically match articles to wellness assessment subcategories using keyword analysis.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={runDryRun}
              disabled={loading}
              variant="outline"
              className="border-burgundy/20 text-burgundy hover:bg-burgundy/5"
              data-testid="auto-tag-preview-btn"
            >
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              {loading ? 'Analyzing...' : 'Preview Tags'}
            </Button>
            {results && results.matched_articles > 0 && (
              <Button
                onClick={applyAllTags}
                disabled={applying}
                className="bg-dark-teal hover:bg-dark-teal/90 text-cream"
                data-testid="auto-tag-apply-all-btn"
              >
                {applying ? 'Applying...' : `Apply All (${results.matched_articles})`}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Results Summary */}
      {results && (
        <div className="bg-white rounded-xl border border-burgundy/15 p-5">
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div className="text-center p-3 bg-cream rounded-lg">
              <p className="text-2xl font-bold text-burgundy">{results.total_articles}</p>
              <p className="text-xs text-burgundy/60">Total Articles</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-700">{results.matched_articles}</p>
              <p className="text-xs text-green-600">Matched</p>
            </div>
            <div className="text-center p-3 bg-burgundy/5 rounded-lg">
              <p className="text-2xl font-bold text-burgundy/60">{results.unmatched_articles}</p>
              <p className="text-xs text-burgundy/40">No Match</p>
            </div>
          </div>

          {results.applied > 0 && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-sm text-green-700">Successfully tagged {results.applied} articles</span>
            </div>
          )}

          {/* Suggestion List */}
          <div className="space-y-2">
            <h4 className="font-semibold text-burgundy text-sm">
              {results.dry_run ? 'Suggested Tags (Preview)' : 'Applied Tags'}
            </h4>
            {results.suggestions.map((suggestion, i) => {
              const isApplied = appliedIds.has(suggestion.article_id);
              const isExpanded = expandedArticle === suggestion.article_id;
              return (
                <div key={suggestion.article_id + i} className={`border rounded-lg overflow-hidden ${isApplied ? 'border-green-200 bg-green-50/50' : 'border-burgundy/10'}`}>
                  <div
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-cream/30"
                    onClick={() => setExpandedArticle(isExpanded ? null : suggestion.article_id)}
                  >
                    {isApplied ? (
                      <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-burgundy/30 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-burgundy truncate">{suggestion.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${CONFIDENCE_COLORS[suggestion.confidence]}`}>
                          {suggestion.confidence}
                        </span>
                        <span className="text-[10px] text-burgundy/50">
                          {suggestion.suggested_subcategory} ({suggestion.suggested_pillar_label})
                        </span>
                      </div>
                    </div>
                    {!isApplied && results.dry_run && (
                      <Button
                        onClick={(e) => { e.stopPropagation(); applySingleTag(suggestion); }}
                        size="sm"
                        className="bg-burgundy hover:bg-burgundy/80 text-cream text-xs h-7 px-3"
                      >
                        Apply
                      </Button>
                    )}
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-burgundy/30" /> : <ChevronDown className="h-4 w-4 text-burgundy/30" />}
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-3 border-t border-burgundy/5 pt-2">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-burgundy/40">Current subcategory:</span>
                          <p className="text-burgundy">{suggestion.current_subcategory || '(none)'}</p>
                        </div>
                        <div>
                          <span className="text-burgundy/40">Current pillar:</span>
                          <p className="text-burgundy">{suggestion.current_pillar || '(none)'}</p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className="text-burgundy/40 text-xs">All matches:</span>
                        <div className="flex gap-1.5 mt-1 flex-wrap">
                          {suggestion.all_matches.map(m => (
                            <span key={m.name} className="text-[10px] px-2 py-0.5 bg-burgundy/5 text-burgundy/70 rounded-full">
                              {m.name} (score: {m.score})
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoTagTool;
